import { useEffect, useRef } from "react";
import { getAdsenseClient, isAdsenseEnabled, loadAdsense, pushAd } from "../lib/adsense";

/**
 * AdSlot — renders a real Google AdSense <ins class="adsbygoogle"> unit
 * when REACT_APP_ADSENSE_CLIENT is set, otherwise renders a sized
 * placeholder (useful during development and while AdSense approval
 * is pending).
 *
 * Props:
 *  - size: "leaderboard" | "mpu" | "half_page" | "banner"
 *  - slot: optional AdSense ad slot id (from AdSense UI). If omitted
 *          the component renders the placeholder regardless of client.
 *  - label / testid: accessibility + testing
 */
const SIZE_MAP = {
  leaderboard: { w: "min-h-[90px]", aspect: "max-w-[728px]", hint: "728×90", format: "horizontal" },
  mpu: { w: "min-h-[250px]", aspect: "max-w-[300px]", hint: "300×250", format: "rectangle" },
  half_page: { w: "min-h-[600px]", aspect: "max-w-[300px]", hint: "300×600", format: "vertical" },
  banner: { w: "min-h-[60px]", aspect: "max-w-[468px]", hint: "468×60", format: "horizontal" },
};

export const AdSlot = ({ size = "leaderboard", slot, label = "Advertisement", testid }) => {
  const cfg = SIZE_MAP[size] || SIZE_MAP.leaderboard;
  const client = getAdsenseClient();
  const live = isAdsenseEnabled() && !!slot;
  const pushed = useRef(false);

  useEffect(() => {
    if (!live) return;
    loadAdsense();
    if (!pushed.current) {
      pushAd();
      pushed.current = true;
    }
  }, [live]);

  return (
    <div
      className="border-y border-foreground/15 bg-muted/30 flex flex-col items-center py-6 my-12"
      data-testid={testid || `ad-slot-${size}`}
    >
      <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-muted-foreground mb-3">
        {label}
      </span>
      {live ? (
        <ins
          className="adsbygoogle"
          style={{ display: "block", width: "100%", maxWidth: cfg.aspect.replace("max-w-[", "").replace("]", "") }}
          data-ad-client={client}
          data-ad-slot={slot}
          data-ad-format={cfg.format}
          data-full-width-responsive="true"
        />
      ) : (
        <div
          className={`w-full ${cfg.aspect} ${cfg.w} mx-auto border border-dashed border-foreground/20 flex items-center justify-center bg-background/50`}
        >
          <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
            Ad Space · {cfg.hint}
          </span>
        </div>
      )}
    </div>
  );
};
